import { useState, useEffect, useRef } from "react";
import api from "../lib/api";
import PodLayout from "../components/PodLayout";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { useAuth } from "../contexts/AuthContext";
import { UsersThree, PaperPlaneRight, Users, Calendar, Plus, Trash, VideoCamera } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function PodRoundtablePage() {
  const { user } = useAuth();
  const isStaff = ["super_admin", "admin"].includes(user?.role);
  const [tables, setTables] = useState([]);
  const [me, setMe] = useState(null);
  const [active, setActive] = useState(null);
  const [members, setMembers] = useState([]);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [meetings, setMeetings] = useState([]);
  const [meetOpen, setMeetOpen] = useState(false);
  const [meetForm, setMeetForm] = useState({ title: "", description: "", starts_at: "", duration_minutes: 60, join_url: "", host: "" });
  const [meetSaving, setMeetSaving] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    api.get("/pod/roundtables").then(r => setTables(r.data || [])).catch(() => {});
    api.get("/pod/me").then(r => { setMe(r.data || {}); if (r.data?.roundtable) setActive(r.data.roundtable); }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!active) return;
    api.get(`/pod/roundtables/${encodeURIComponent(active)}/members`).then(r => setMembers(r.data || [])).catch(() => setMembers([]));
    loadMessages();
    loadMeetings();
    const interval = setInterval(loadMessages, 8000);
    return () => clearInterval(interval);
  }, [active]);

  const loadMeetings = async () => {
    if (!active) return;
    try {
      const r = await api.get(`/pod/roundtables/${encodeURIComponent(active)}/meetings`);
      setMeetings(r.data || []);
    } catch { setMeetings([]); }
  };

  const scheduleMeeting = async () => {
    if (!meetForm.title.trim() || !meetForm.starts_at) { toast.error("Title and start time required"); return; }
    setMeetSaving(true);
    try {
      await api.post(`/pod/roundtables/${encodeURIComponent(active)}/meetings`, {
        ...meetForm,
        duration_minutes: parseInt(meetForm.duration_minutes, 10) || 60,
      });
      toast.success("Meeting scheduled and members notified");
      setMeetOpen(false);
      setMeetForm({ title: "", description: "", starts_at: "", duration_minutes: 60, join_url: "", host: "" });
      loadMeetings();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to schedule"); }
    setMeetSaving(false);
  };

  const deleteMeeting = async (id) => {
    if (!window.confirm("Cancel this meeting?")) return;
    try { await api.delete(`/pod/roundtables/${encodeURIComponent(active)}/meetings/${id}`); toast.success("Cancelled"); loadMeetings(); }
    catch { toast.error("Delete failed"); }
  };

  const loadMessages = async () => {
    if (!active) return;
    try {
      const r = await api.get(`/pod/roundtables/${encodeURIComponent(active)}/messages`);
      setMessages(r.data || []);
      setTimeout(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, 100);
    } catch (e) { /* ignore */ }
  };

  const join = async (name) => {
    if (isStaff) {
      // Staff browse roundtables without joining as subscribers.
      setActive(name);
      return;
    }
    try { await api.post("/pod/me/roundtable", { name }); setMe({ ...(me || {}), roundtable: name }); setActive(name); toast.success(`Joined ${name}`); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const send = async () => {
    if (!input.trim() || !active) return;
    try {
      await api.post(`/pod/roundtables/${encodeURIComponent(active)}/messages`, { text: input });
      setInput("");
      loadMessages();
    } catch (e) { toast.error(e.response?.data?.detail || "Send failed"); }
  };

  return (
    <PodLayout>
      <div className="p-6 md:p-8 lg:p-12" data-testid="pod-roundtable-page">
        <div className="mb-6">
          <p className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-1">POD</p>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-950" style={{ fontFamily: "'Outfit', sans-serif" }}>Virtual Roundtables</h1>
          <p className="text-sm text-slate-600 mt-1">Professional communities for peer learning, sharing, and networking.</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          {/* Sidebar: Roundtables */}
          <div className="space-y-2">
            <p className="text-xs uppercase tracking-widest text-slate-500 font-bold">Roundtables</p>
            {tables.map(t => (
              <button key={t.name} onClick={() => active === t.name ? null : join(t.name)} data-testid={`roundtable-${t.name.replace(/\s+/g, "-").toLowerCase()}`} className={`w-full text-left px-3 py-2 border ${active === t.name ? "bg-blue-50 border-blue-800 text-blue-900" : "bg-white border-slate-200 hover:border-blue-300"}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold">{t.name}</p>
                    <p className="text-xs text-slate-500">{t.member_count} member{t.member_count === 1 ? "" : "s"}</p>
                  </div>
                  {me?.roundtable === t.name && <span className="text-[10px] uppercase font-bold tracking-wider text-emerald-600">Mine</span>}
                </div>
              </button>
            ))}
            {tables.length === 0 && <p className="text-sm text-slate-500">No roundtables.</p>}
          </div>

          {/* Main: Chat + Members */}
          <div className="lg:col-span-3">
            {!active ? (
              <div className="bg-white border border-dashed border-slate-300 p-12 text-center">
                <UsersThree size={64} weight="duotone" className="mx-auto text-slate-300 mb-3" />
                <p className="text-sm text-slate-500">Pick a roundtable from the left to join the conversation.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Roundtable meetings */}
                <Card className="rounded-none border border-slate-200">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
                      <p className="text-xs uppercase tracking-widest text-slate-500 font-bold flex items-center gap-1"><Calendar size={12} /> {active} Meetings</p>
                      {isStaff && (
                        <Button onClick={() => setMeetOpen(true)} className="bg-amber-500 hover:bg-amber-600 text-slate-950 text-xs gap-1" data-testid="schedule-roundtable-meeting-btn">
                          <Plus size={14} /> Schedule meeting
                        </Button>
                      )}
                    </div>
                    {meetings.length === 0 && <p className="text-xs text-slate-500">No meetings scheduled yet.</p>}
                    <div className="space-y-2">
                      {meetings.map(m => {
                        const dt = m.starts_at ? new Date(m.starts_at) : null;
                        const past = dt && dt.getTime() < Date.now();
                        return (
                          <div key={m.id} className={`border ${past ? "border-slate-200 bg-slate-50" : "border-amber-200 bg-amber-50"} px-3 py-2 flex items-start justify-between gap-3`} data-testid={`roundtable-meeting-${m.id}`}>
                            <div className="min-w-0">
                              <p className="text-sm font-bold text-slate-950 truncate">{m.title}</p>
                              <p className="text-[11px] text-slate-600 mt-0.5">
                                {dt ? dt.toLocaleString() : "TBA"} · {m.duration_minutes} min · {m.host || "TBA"}
                              </p>
                              {m.description && <p className="text-[11px] text-slate-600 mt-1 line-clamp-2">{m.description}</p>}
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              {!past && m.join_url && (
                                <a href={m.join_url} target="_blank" rel="noreferrer">
                                  <Button className="bg-slate-950 hover:bg-slate-800 text-white text-xs gap-1" data-testid={`join-roundtable-meeting-${m.id}`}>
                                    <VideoCamera size={12} /> Join
                                  </Button>
                                </a>
                              )}
                              {!past && (
                                <a
                                  href={`${process.env.REACT_APP_BACKEND_URL}/api/pod/roundtables/${encodeURIComponent(active)}/meetings/${m.id}/ics`}
                                  className="inline-flex items-center gap-1 text-xs px-2 py-1.5 border border-slate-300 text-slate-700 hover:border-amber-400 hover:text-amber-700 transition-colors"
                                  data-testid={`ics-roundtable-meeting-${m.id}`}
                                  title="Add to Google / Apple / Outlook calendar"
                                >
                                  <Calendar size={12} /> Add to calendar
                                </a>
                              )}
                              {isStaff && (
                                <button onClick={() => deleteMeeting(m.id)} className="text-red-600 hover:text-red-800" data-testid={`cancel-roundtable-meeting-${m.id}`}>
                                  <Trash size={14} />
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Chat */}
                <Card className="md:col-span-2 rounded-none border border-slate-200 h-[500px] flex flex-col">
                  <div className="bg-blue-800 text-white px-4 py-3 font-bold text-sm flex items-center gap-2"><UsersThree weight="fill" /> {active}</div>
                  <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-3 bg-slate-50">
                    {messages.length === 0 && <p className="text-xs text-slate-400 text-center mt-12">No messages yet. Be the first.</p>}
                    {messages.map(m => (
                      <div key={m.id} className={`flex ${m.user_id === user?.id ? "justify-end" : "justify-start"}`}>
                        <div className={`max-w-[80%] px-3 py-2 text-sm ${m.user_id === user?.id ? "bg-blue-800 text-white" : "bg-white border border-slate-200 text-slate-800"}`}>
                          {m.user_id !== user?.id && <p className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">{m.author_name}</p>}
                          <p className="whitespace-pre-wrap break-words">{m.text}</p>
                          <p className={`text-[10px] mt-1 ${m.user_id === user?.id ? "text-blue-200" : "text-slate-400"}`}>{new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="border-t border-slate-200 p-2 flex gap-2 bg-white">
                    <Input value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => e.key === "Enter" && send()} placeholder={`Message ${active}…`} className="rounded-none text-sm flex-1" data-testid="roundtable-msg-input" />
                    <Button onClick={send} className="bg-blue-800 text-white hover:bg-blue-900 rounded-none" data-testid="roundtable-send"><PaperPlaneRight weight="fill" size={14} /></Button>
                  </div>
                </Card>

                {/* Members */}
                <Card className="rounded-none border border-slate-200 h-[500px] overflow-y-auto">
                  <CardContent className="p-4">
                    <p className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-2 flex items-center gap-1"><Users size={12} /> Members ({members.length})</p>
                    {members.map(m => (
                      <div key={m.user_id} className="py-2 border-b border-slate-100 last:border-0">
                        <p className="text-sm font-semibold text-slate-950">{m.full_name}</p>
                        <p className="text-xs text-slate-500">{m.job_title || "—"}</p>
                        <p className="text-[10px] text-slate-400">{m.school_name}{m.city ? ` · ${m.city}` : ""}</p>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </div>
              </div>
            )}
          </div>
        </div>

        {/* Schedule Meeting Dialog (staff only) */}
        <Dialog open={meetOpen} onOpenChange={setMeetOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>Schedule {active} Roundtable Meeting</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label className="text-xs font-bold uppercase tracking-widest">Title</Label>
                <Input value={meetForm.title} onChange={e => setMeetForm({ ...meetForm, title: e.target.value })} placeholder="Monthly leadership huddle" data-testid="rt-meet-title" /></div>
              <div><Label className="text-xs font-bold uppercase tracking-widest">Description (agenda)</Label>
                <Textarea rows={3} value={meetForm.description} onChange={e => setMeetForm({ ...meetForm, description: e.target.value })} data-testid="rt-meet-description" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs font-bold uppercase tracking-widest">Starts at</Label>
                  <Input type="datetime-local" value={meetForm.starts_at} onChange={e => setMeetForm({ ...meetForm, starts_at: e.target.value })} data-testid="rt-meet-starts" /></div>
                <div><Label className="text-xs font-bold uppercase tracking-widest">Duration (mins)</Label>
                  <Input type="number" value={meetForm.duration_minutes} onChange={e => setMeetForm({ ...meetForm, duration_minutes: e.target.value })} data-testid="rt-meet-duration" /></div>
              </div>
              <div><Label className="text-xs font-bold uppercase tracking-widest">Host</Label>
                <Input value={meetForm.host} onChange={e => setMeetForm({ ...meetForm, host: e.target.value })} placeholder="Facilitator name" data-testid="rt-meet-host" /></div>
              <div><Label className="text-xs font-bold uppercase tracking-widest">Join URL</Label>
                <Input value={meetForm.join_url} onChange={e => setMeetForm({ ...meetForm, join_url: e.target.value })} placeholder="https://meet.google.com/…" data-testid="rt-meet-join-url" /></div>
              <Button onClick={scheduleMeeting} disabled={meetSaving} className="w-full bg-slate-950 hover:bg-slate-800 text-white" data-testid="rt-meet-submit">
                {meetSaving ? "Scheduling…" : "Schedule & notify members"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </PodLayout>
  );
}
